/**
 * The browser sends only this opaque digest to Logto Core. Core adds the trusted
 * client IP and calls Identity Bridge with its server-side bearer credential.
 */
export const aletynBrowserFingerprintHeader = 'X-Aletyn-Browser-Fingerprint';

const storageKey = 'aletyn_sms_browser_id';
const fingerprintVersion = 'aletyn-sms-v1';

const bytesToHex = (bytes: Uint8Array): string =>
  Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');

const getPersistentBrowserId = (): string => {
  try {
    const existing = window.localStorage.getItem(storageKey);

    if (existing && /^[\da-f]{48}$/i.test(existing)) {
      return existing.toLowerCase();
    }

    const generated = bytesToHex(window.crypto.getRandomValues(new Uint8Array(24)));
    window.localStorage.setItem(storageKey, generated);
    return generated;
  } catch {
    // Private or sandboxed contexts can deny localStorage. The request still has
    // hard server-side IP and phone buckets; this value scopes only this page load.
    return bytesToHex(window.crypto.getRandomValues(new Uint8Array(24)));
  }
};

/* eslint-disable @silverhand/fp/no-mutation -- Canvas rendering is an imperative browser API. */
const getCanvasTrait = (): string => {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 160;
    canvas.height = 40;

    const context = canvas.getContext('2d');
    if (!context) {
      return '';
    }

    context.textBaseline = 'alphabetic';
    context.fillStyle = '#60ddf9';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = '#5a3efc';
    context.font = '14px Arial, sans-serif';
    context.fillText('Aletyn SMS fingerprint v1', 4, 22);

    return canvas.toDataURL().slice(0, 160);
  } catch {
    return '';
  }
};
/* eslint-enable @silverhand/fp/no-mutation */

const getTimezone = (): string => {
  try {
    return new Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return '';
  }
};

const normalizeTrait = (value: string, maxLength: number): string =>
  value.slice(0, maxLength).replace(/\s+/g, ' ').trim();

const buildFingerprintSource = (): string => {
  const deviceMemory =
    'deviceMemory' in navigator ? String(Reflect.get(navigator, 'deviceMemory') ?? '') : '';

  return [
    fingerprintVersion,
    getPersistentBrowserId(),
    normalizeTrait(navigator.userAgent, 240),
    normalizeTrait(navigator.language, 32),
    normalizeTrait(navigator.platform, 64),
    normalizeTrait(getTimezone(), 64),
    String(navigator.hardwareConcurrency),
    deviceMemory,
    `${screen.width}x${screen.height}x${screen.colorDepth}x${window.devicePixelRatio || 1}`,
    getCanvasTrait(),
  ].join('\n');
};

/**
 * Returns a 64-character SHA-256 digest. If Web Crypto is unavailable, Core
 * derives a server-side fallback from the trusted IP and user agent instead.
 */
export const getSmsBrowserFingerprint = async (): Promise<string | undefined> => {
  try {
    const source = new TextEncoder().encode(buildFingerprintSource());
    const digest = await window.crypto.subtle.digest('SHA-256', source);
    return bytesToHex(new Uint8Array(digest));
  } catch {
    return undefined;
  }
};

export const getAletynSmsFingerprintHeaders = async (
  identifierType: string
): Promise<Record<string, string> | undefined> => {
  if (identifierType !== 'phone') {
    return;
  }

  const browserFingerprint = await getSmsBrowserFingerprint();

  return browserFingerprint ? { [aletynBrowserFingerprintHeader]: browserFingerprint } : undefined;
};
